import type { PDFPage } from "pdf-lib";

/**
 * Where things go on a page *as the reader sees it*. A PDF page can carry a /Rotate (scans from a phone
 * or scanner often do) and a crop box smaller than its media box; viewers show the rotated, cropped page,
 * but drawing coordinates are in the stored, unrotated space. Stamping at "bottom-right" in stored
 * coordinates puts the number on the wrong edge of a rotated page, or off the visible area (WM-1, PN-1).
 *
 * `pageView(page)` describes the visible page: its width/height as seen, and `toStored` which turns a point
 * measured from the viewed bottom-left corner into stored coordinates. `rotation` is also the angle to add
 * to anything drawn so that it reads upright in the viewer.
 */
export interface PageView {
  /** Width and height of the page as the reader sees it. */
  width: number;
  height: number;
  rotation: 0 | 90 | 180 | 270;
  /** A viewed point (origin bottom-left of the visible page, y up) in stored page coordinates. */
  toStored(vx: number, vy: number): { x: number; y: number };
  /** The reverse of toStored: a stored page point as the reader sees it. */
  toViewed(sx: number, sy: number): { x: number; y: number };
}

export function pageView(page: PDFPage): PageView {
  const box = page.getCropBox();
  const r = ((((Math.round(page.getRotation().angle / 90) * 90) % 360) + 360) % 360) as 0 | 90 | 180 | 270;
  const { x: x0, y: y0, width: w, height: h } = box;
  const swap = r === 90 || r === 270;
  return {
    width: swap ? h : w,
    height: swap ? w : h,
    rotation: r,
    toStored(vx, vy) {
      switch (r) {
        case 90: return { x: x0 + (w - vy), y: y0 + vx };
        case 180: return { x: x0 + (w - vx), y: y0 + (h - vy) };
        case 270: return { x: x0 + vy, y: y0 + (h - vx) };
        default: return { x: x0 + vx, y: y0 + vy };
      }
    },
    toViewed(sx, sy) {
      switch (r) {
        case 90: return { x: sy - y0, y: w - (sx - x0) };
        case 180: return { x: w - (sx - x0), y: h - (sy - y0) };
        case 270: return { x: h - (sy - y0), y: sx - x0 };
        default: return { x: sx - x0, y: sy - y0 };
      }
    },
  };
}

/** A stored-space rectangle for a viewed rectangle (origin bottom-left of the visible page): used to set a crop box. */
export function storedRectFromViewed(view: PageView, vx: number, vy: number, vw: number, vh: number): { x: number; y: number; width: number; height: number } {
  const a = view.toStored(vx, vy);
  const b = view.toStored(vx + vw, vy + vh);
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width: Math.abs(a.x - b.x), height: Math.abs(a.y - b.y) };
}
